/**
 * Weather service — Open-Meteo free forecast API
 * No API key required. See https://open-meteo.com/
 */

/**
 * @typedef {Object} WindHour
 * @property {string} time - ISO timestamp
 * @property {number|null} directionDeg - Wind direction in degrees
 * @property {number|null} speedKnots - Wind speed in knots
 * @property {number|null} gustsKnots - Wind gusts in knots
 * @property {number|null} precipProb - Precipitation probability %
 * @property {number|null} weatherCode - WMO weather code
 */

/**
 * Fetch wind forecast from Open-Meteo
 * @param {number} latitude
 * @param {number} longitude
 * @param {AbortSignal} [signal] - Optional abort signal
 * @returns {Promise<WindHour[]>}
 */
export async function getWindForecast(latitude, longitude, signal) {
  const params = new URLSearchParams({
    latitude: String(latitude),
    longitude: String(longitude),
    hourly: [
      'wind_direction_10m',
      'wind_speed_10m',
      'wind_gusts_10m',
      'precipitation_probability',
      'weather_code',
    ].join(','),
    forecast_hours: '12',
    timezone: 'Australia/Sydney',
    wind_speed_unit: 'kn',
  });

  const url = `https://api.open-meteo.com/v1/forecast?${params.toString()}`;

  const response = await fetch(url, { signal });

  if (!response.ok) {
    throw new Error(`Weather API error: ${response.status}`);
  }

  const data = await response.json();
  const { hourly } = data;

  return hourly.time.map((time, i) => ({
    time,
    directionDeg: hourly.wind_direction_10m[i],
    speedKnots: hourly.wind_speed_10m[i],
    gustsKnots: hourly.wind_gusts_10m[i],
    precipProb: hourly.precipitation_probability[i],
    weatherCode: hourly.weather_code[i],
  }));
}

/**
 * Convert knots to km/h
 * @param {number|null} knots
 * @returns {number|null}
 */
export function knotsToKmh(knots) {
  return knots !== null ? Math.round(knots * 1.852) : null;
}

/**
 * Convert wind degrees to 16-point compass direction
 * @param {number|null} degrees
 * @returns {string}
 */
export function compassDirection(degrees) {
  if (degrees === null) return '—';
  const dirs = [
    'N', 'NNE', 'NE', 'ENE',
    'E', 'ESE', 'SE', 'SSE',
    'S', 'SSW', 'SW', 'WSW',
    'W', 'WNW', 'NW', 'NNW',
  ];
  return dirs[Math.round(degrees / 22.5) % 16];
}

/**
 * Describe a WMO weather code in plain text
 * @param {number|null} code
 * @returns {string}
 */
export function describeWeather(code) {
  if (code === null) return '—';
  if (code === 0) return '☀ Clear';
  if (code === 1) return '🌤 Mainly clear';
  if (code === 2) return '⛅ Partly cloudy';
  if (code === 3) return '☁ Overcast';
  if (code >= 45 && code <= 48) return '🌫 Foggy';
  if (code >= 51 && code <= 55) return '💧 Drizzle';
  if (code >= 56 && code <= 57) return '🧊 Freezing drizzle';
  if (code >= 61 && code <= 65) return '🌧 Rain';
  if (code >= 66 && code <= 67) return '🧊 Freezing rain';
  if (code >= 71 && code <= 77) return '❄ Snow';
  if (code >= 80 && code <= 82) return '🌦 Rain showers';
  if (code >= 85 && code <= 86) return '❄ Snow showers';
  if (code >= 95) return '⛈ Thunderstorm';
  return `Code ${code}`;
}